/**
 * InvoicesTab — Job-detail Invoices tab (F2).
 *
 * F2 spec:
 * - Tab on /jobs/:id
 * - Invoice list: invoice #, amount, terms, invoiced, due, status/aging pill, overflow
 * - Past-due pill shows days overdue
 * - Row click → /invoices/:id (PT-24 polishes the destination)
 *
 * PT-23 (2026-07-26) — box 23.3: wired the real fetch, replacing the
 *   `setInvoices([])` stub that made the entire invoice surface unreachable.
 *
 * Four distinct states — operator ruling Q4 (2026-07-26):
 *   • Loading  → SkeletonCard (mirrors JobDetail loading treatment).
 *   • Error    → <Alert variant="error"> with inline Retry — canonical shape
 *                per JobsPage / JobDetail / WorkOrderDetail. Explicitly NOT
 *                the same treatment as empty; the prior silent `catch {}`
 *                collapsed error into empty ("No invoices yet"), which told
 *                the operator something false about their data. That §12
 *                violation is closed here.
 *   • Empty    → <EmptyState mode="first-run"> + New Invoice CTA.
 *   • Populated → <Table> (existing render, defensive snake+camel probes).
 *
 * Status vocabulary — PT-23 box 23.5 alignment:
 *   The aging pill uses `deriveAging` (returns `Unsent` for the `draft`
 *   base state, matching the `invoice_status.v1` seed label). Tone is
 *   passed explicitly from `deriveAging` (`aging.tone`) — do NOT rely on
 *   the atom-level `STATUS_TONE_MAP`, which has `draft: 'warning'` correct
 *   for change orders / quote drafts but wrong for invoices. Invoice
 *   surfaces contract on the explicit-tone pattern.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import StatusPill from '../ui/StatusPill';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Alert } from '../ui/Alert';
import { SkeletonCard } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import { invoiceAPI } from '../../services/api';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { deriveAging } from '../invoices/invoiceLifecycle';
import { formatInvoiceNumberFor } from '../../utils/displayId';
import { useSettingsContext } from '../../hooks/useSettings';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/formatDate';
import InvoiceRowMenu from '../invoices/InvoiceRowMenu';
import useTableSort from '../ui/useTableSort';

export default function InvoicesTab({ jobId }) {
  const navigate = useNavigate();
  const { getEnumLabel } = useSettingsContext();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // IDI-538: what's left to bill, from the same server pricing Review & Send
  // and the over_remaining guard use. null = unknown → no button.
  const [remainingCents, setRemainingCents] = useState(null);

  // Wrap fetch in a stable callback so the Retry button can re-invoke it.
  // IDI-518: `silent` refreshes in place after a row action (no skeleton).
  const loadInvoices = useCallback(async ({ silent = false } = {}) => {
    if (!jobId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      // [endpoint-check] Behavioural: `invoiceAPI.getInvoices({job_id})` hits
      // `GET /v1/invoices?job_id=<jobId>`. Backend `cmd/invoices/main.go
      // listInvoices` (line 106) queries the CompanyIndex on `companyId` and
      // returns ALL tenant invoices as a raw JSON array — it does NOT filter
      // by job_id server-side. Client-side filter below is mandatory, not
      // defensive. Response shape after `apiClient.get()` unwraps
      // `response.data.data`: bare array `[invoice, ...]`.
      const res = await invoiceAPI.getInvoices({ job_id: jobId });
      const all = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
      // Filter defensively — accept snake_case + camelCase for job_id.
      const forThisJob = all.filter((inv) => {
        const invJobId = inv.job_id ?? inv.jobId;
        return String(invJobId) === String(jobId);
      });
      setInvoices(forThisJob);
      if (forThisJob.length > 0) {
        try {
          const pr = await invoiceAPI.previewInvoice({ job_id: jobId });
          const p = pr?.data || pr;
          setRemainingCents(typeof p?.remainingCents === 'number' ? p.remainingCents : null);
        } catch {
          setRemainingCents(null);
        }
      }
    } catch (e) {
      setError(e?.message || 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // IDI-479: header click-to-sort. Each accessor reads the same value the
  // cell renders; Amount by cents, Invoiced/Due chronologically.
  const invoiceSortColumns = [
    { key: 'number', type: 'text', value: (inv) => formatInvoiceNumberFor(inv, inv.invoiceNumber || inv.invoice_number) || null },
    { key: 'amount', type: 'money', value: (inv) => inv.total_amount ?? inv.totalAmount ?? inv.amount ?? 0 },
    { key: 'terms', type: 'text', value: (inv) => getEnumLabel('payment_terms', inv.terms) || inv.terms || null },
    { key: 'status', type: 'text', value: (inv) => deriveAging(inv)?.aging || null },
    { key: 'invoiced', type: 'date', value: (inv) => inv.invoiced_date || inv.invoicedDate || null },
    { key: 'due', type: 'date', value: (inv) => inv.due_date || inv.dueDate || null },
  ];
  const { sortedRows: sortedInvoices, getSortProps } = useTableSort(invoices, invoiceSortColumns);

  return (
    <div className="space-y-4">
      {/* State 1 — Loading */}
      {loading && <SkeletonCard />}

      {/* State 2 — Error (canonical Alert shape + inline Retry).
          Explicitly rendered BEFORE the empty-check so a fetch failure never
          falls through to "No invoices yet" (the prior §12 violation). */}
      {!loading && error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          <div className="flex items-center justify-between gap-4">
            <span>{error}</span>
            <Button size="sm" variant="secondary" onClick={() => loadInvoices()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* State 3 — Empty (genuinely empty, not error). */}
      {!loading && !error && invoices.length === 0 && (
        <Card>
          <EmptyState
            mode="first-run"
            icon={BanknotesIcon}
            title="No invoices yet"
            description="Invoices generated from this job will appear here. Create the first one when the work is complete or ready to bill."
            action={
              <Button
                variant="secondary"
                onClick={() => navigate(`/jobs/${jobId}/invoices/new`)}
              >
                + New Invoice
              </Button>
            }
          />
        </Card>
      )}

      {/* State 4 — Populated. */}
      {!loading && !error && invoices.length > 0 && remainingCents > 0 && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            data-testid="invoices-tab-new-invoice"
            onClick={() => navigate(`/jobs/${jobId}/invoices/new`)}
          >
            + New Invoice
          </Button>
        </div>
      )}
      {!loading && !error && invoices.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader {...getSortProps('number')}>Invoice #</TableHeader>
              <TableHeader align="right" {...getSortProps('amount')}>Amount</TableHeader>
              <TableHeader {...getSortProps('terms')}>Terms</TableHeader>
              <TableHeader {...getSortProps('status')}>Status</TableHeader>
              <TableHeader {...getSortProps('invoiced')}>Invoiced</TableHeader>
              <TableHeader {...getSortProps('due')}>Due</TableHeader>
              <TableHeader align="right"><span className="sr-only">Actions</span></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInvoices.map((inv) => {
              const aging = deriveAging(inv);
              const invNumber = inv.invoiceNumber || inv.invoice_number;
              const invoicedDate = inv.invoiced_date || inv.invoicedDate;
              const dueDate = inv.due_date || inv.dueDate;
              // PT-23: cents → dollars unit unchanged from prior shape.
              // Amount defensive read: total_amount ?? amount ?? 0 (cents).
              const amountCents = inv.total_amount ?? inv.totalAmount ?? inv.amount ?? 0;
              // PT-23 23.2: terms label via canonical `payment_term.v1` seed
              // (no local shadow). Legacy `net_10` falls through to
              // `prettifyEnum('net_10') → 'Net 10'` — acceptable read-side compat.
              const termsLabel = getEnumLabel('payment_terms', inv.terms) || inv.terms;
              return (
                <TableRow
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell>{formatInvoiceNumberFor(inv, invNumber)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(amountCents)}
                  </TableCell>
                  <TableCell>{termsLabel}</TableCell>
                  <TableCell data-testid="invoice-row-status">
                    <StatusPill tone={aging.tone}>{aging.aging}</StatusPill>
                  </TableCell>
                  {/* IDI-337: IDI-101's formatter (bare dates read as local), same as the detail. */}
                  {/* IDI-519: the send (or first download) date; "—" until then. */}
                  <TableCell data-testid="invoice-row-invoiced-date">{formatDate(invoicedDate) || "—"}</TableCell>
                  <TableCell data-testid="invoice-row-due-date">{formatDate(dueDate) || "—"}</TableCell>
                  {/* IDI-518: row ⋮; stopPropagation keeps row click = open. */}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} data-testid="invoice-row-actions">
                    <InvoiceRowMenu invoice={inv} onChanged={() => loadInvoices({ silent: true })} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
